import { useEffect, useRef, useState } from 'react'

import { useLocation, useNavigate } from 'react-router'
import { useThrottledCallback } from 'use-debounce'
import { isFunction } from '@/utils/misc'
import { isIsoDateString } from './useAxiosInterceptors'
import { useLocationState } from './useLocationState'

// ///////////////////////
// MARK: TYPES
// @ts-ignore
type TValue =
  | Record<string, TValue>
  | TValue[]
  | string
  | number
  | boolean
  | undefined
  | null
  | Date

// ///////////////////////
// MARK: EXPORTS
export function useStorageState<S extends TValue>(
  type: 'local' | 'session' | 'location',
  key: string,
  initialState: S | ((key?: string) => S),
  validation?: (value: S) => S,
): [state: S, setState: React.Dispatch<React.SetStateAction<S>>] {
  const location = useLocation()
  const navigate = useNavigate()

  const locationState = useLocationState<{
    [key: string]: string
  }>()

  const [state, setState] = useState<S>(() => {
    const actualKey = `state_${key}`
    try {
      const value =
        type === 'local'
          ? localStorage.getItem(`state_${key}`)
          : type === 'session'
            ? sessionStorage.getItem(actualKey)
            : locationState[actualKey]

      if (value) {
        const parsedValue = JSON.parse(value, (jsonKey, jsonValue: unknown) => {
          if (typeof jsonValue === 'string') {
            if (isIsoDateString(jsonValue)) {
              return new Date(jsonValue)
            }

            if (jsonKey === 'clazz') {
              return jsonValue.split('_')[0]
            }
          }

          return jsonValue
        }) as S

        if (validation) {
          return validation(parsedValue)
        }

        return parsedValue
      }
    } catch {
      // NOTHING
    }

    return isFunction(initialState) ? initialState(actualKey) : initialState
  })

  const savedState = useRef<S | null>(null)

  const throttledSaveState = useThrottledCallback(() => {
    savedState.current = state

    const actualKey = `state_${key}`
    const actualState = JSON.stringify(state)

    switch (type) {
      case 'local': {
        localStorage.setItem(actualKey, actualState)

        break
      }
      case 'session': {
        sessionStorage.setItem(actualKey, actualState)

        break
      }
      case 'location': {
        navigate(
          { ...location },
          {
            replace: true,
            state: {
              ...locationState,
              [actualKey]: actualState,
            },
          },
        )

        break
      }
      default: {
        break
      }
    }
  }, 500)

  // Set state to Storage
  useEffect(() => {
    if (state !== savedState.current) {
      throttledSaveState()
    }
  }, [state, throttledSaveState])

  // Return
  return [state, setState]
}
